import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Équité",
  "lead": "Des progrès ont été accomplis à l’échelle mondiale en matière de parité entre les sexes dans l’éducation, mais les écarts demeurent profonds entre le milieu rural et le milieu urbain, et entre les riches et les pauvres."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, PageLink, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!PageLink) _missingMdxReference("PageLink", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_EQUITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "La volonté de ne laisser personne de côté, qui recoupe l’ensemble des Objectifs de développement durable (ODD), est expressément mentionnée dans la 5e cible de l’ODD relatif à l’éducation, l’ODD 4. Il s’agit de faire en sorte que les responsables politiques aident en priorité ceux qui accusent le plus grand retard."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Pour être en mesure de déterminer si les écarts en matière d’éducation sont en passe d’être comblés, nous avons besoin de données ventilées ", _jsx(_components.strong, {
        children: "par niveau de revenu, sexe, âge, race, groupe ethnique, statut migratoire, handicap, situation géographique et d’autres caractéristiques"
      }), " selon les contextes nationaux, que fournissent les enquêtes auprès des ménages."]
    }), "\n", _jsxs(_components.p, {
      children: ["Depuis 2010, la ", _jsx(_components.a, {
        href: "https://www.education-inequalities.org/",
        children: "World Inequality Database on Education"
      }), " s’appuie sur les enquêtes menées auprès des ménages pour faire apparaître l’ampleur des écarts en matière d’éducation dans différents pays. Cet article récapitule quelques-uns des faits et tendances majeurs en matière d’inégalité dans l’éducation, tandis que certains aspects complémentaires sont abordés dans l’article sur le ", _jsx(PageLink, {
        name: "finance",
        children: "financement"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "égalité-des-genres",
      children: "Égalité des genres"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.5.1",
      children: [_jsxs(_components.p, {
        children: ["Le graphique ci-après présente les inégalités de l’éducation entre groupes, tels que les filles et les garçons, ou les plus démunis et les plus riches. Il mesure ces écarts en appliquant un ", _jsx(Tooltip, {
          title: "indice de parité",
          content: "Si un indice de parité est proche de 0, cela signifie que le groupe défavorisé est très loin derrière ; si l’indice est proche de 1, la parité est atteinte ; si l’indice est proche de 2, le groupe favorisé est le plus en retard. Si, par exemple, l’indice de parité est de 0,5, cela peut signifier que 30 % des filles et 60 % des garçons sont scolarisés. Un indice de 1,5 signifierait qu’il y a 30 % de garçons et 60 % de filles scolarisés.",
          children: "indice de parité"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Bien que ce fait soit rarement salué, la ", _jsx(Tooltip, {
          title: "parité entre les sexes ",
          content: "Lorsqu’il y a autant de filles que de garçons scolarisés",
          children: "parité entre les sexes"
        }), " n’a cessé de s’améliorer au cours de la dernière génération. Dans de nombreuses régions du monde, généralement les plus riches, les filles et les jeunes femmes surpassent en nombre les garçons et les jeunes hommes dans les écoles et les universités."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "GPITREND"
          })
        }), _jsx(ScrollySection, {
          id: "income,0",
          children: _jsxs(_components.p, {
            children: ["Toute ligne en-deçà de ", _jsx(HighlightByIndex, {
              index: "-1",
              children: "la zone grisée"
            }), " indique que le nombre de garçons et de jeunes gens scolarisés est supérieur à celui des filles."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,1",
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "0",
              children: "Afrique subsaharienne"
            }), ", l’enseignement tertiaire compte encore moins de 4 jeunes femmes pour 5 hommes."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,2",
          children: _jsxs(_components.p, {
            children: [_jsx(HighlightByIndex, {
              index: "2",
              children: "L’Asie centrale et l’Asie du Sud"
            }), ", où les filles accusaient un important retard en 1990, ont désormais atteint la parité entre les sexes dans le l’enseignement primaire au l’enseignement tertiaire, principalement grâce aux spectaculaires progrès réalisés en Inde. En conséquence, la région a dépassé ", _jsx(HighlightByIndex, {
              index: "0",
              children: "l’Afrique subsaharienne"
            }), " en termes de progrès vers la réalisation de la parité entre les sexes."]
          })
        })]
      }), _jsx(_components.p, {
        children: "À l’échelle mondiale, d’importants progrès ont été réalisés depuis 2000. Cependant, un examen approfondi au niveau des pays révèle des écarts persistants : seuls trois pays sur quatre ont atteint la parité entre les sexes concernant la scolarisation dans le primaire, près d'un pays sur deux dans le premier cycle du secondaire et moins d'un pays sur trois dans le second cycle du secondaire."
      }), _jsxs(_components.p, {
        children: ["Bien qu’il ait diminué au moins de moitié depuis 2000 à l’enseignement primaire, secondaire et tertiaire, le pourcentage de pays affichant de ", _jsx(HighlightByIndex, {
          index: "2",
          children: "fortes disparités au détriment des filles "
        }), " est toujours de 2 % dans le primaire, de 6 % dans le premier cycle du secondaire et de 9 % dans le second cycle du secondaire."]
      }), _jsxs(_components.p, {
        children: ["Nous ne devons pas oublier les garçons en évoquant l’égalité des genres. S’il est moins courant pour des pays de présenter d' ", _jsx(HighlightByIndex, {
          index: "0",
          children: "inégalités au détriment des garçons"
        }), " dans le primaire, ces inégalités sont bien plus répandues dans le second cycle du secondaire et au-delà."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PARITYCOUNT"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["Dans ce graphique, plus la partie bleue de la barre est large et foncée, plus nombreux sont les pays ", _jsx(HighlightByIndex, {
              index: "0",
              children: "comptant davantage de garçons que de filles parmi les enfants déscolarisés"
            }), " than girls. Plus la partie verte de la barre est large et foncée, plus nombreux sont les pays qui ", _jsx(HighlightByIndex, {
              index: "2",
              children: "comptent davantage de filles déscolarisées que de garçons "
            }), " dans cette situation."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["Dans l’enseignement tertiaire, par exemple, trois quarts des pays présentent de ", _jsx(HighlightByIndex, {
              index: "0",
              children: " profondes disparités au détriment des garçons"
            }), "."]
          })
        })]
      }), _jsx(_components.h2, {
        id: "richesse",
        children: "Richesse"
      }), _jsx(_components.p, {
        children: "Dans l’ensemble, les écarts entre filles et garçons en matière d’éducation ne sont pas aussi profonds que les écarts entre le milieu rural et le milieu urbain, ou entre les riches et les pauvres, comme l’ont fait ressortir les enquêtes auprès des ménages."
      }), _jsxs(_components.p, {
        children: ["À Madagascar, par exemple, si 85 % des ", _jsx(HighlightByIndex, {
          index: "0",
          children: "plus riches "
        }), " suivent un enseignement primaire, ils ne sont plus que 13 % parmi les ", _jsx(HighlightByIndex, {
          index: "1",
          children: "plus pauvres"
        }), ". Au Nigéria, 94 % des plus riches fréquentent le premier cycle de l’enseignement secondaire, contre seulement 27 % parmi les plus pauvres."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PSECWGAP"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["Les points rouges représentent le pourcentage d’enfants ou de jeunes les ", _jsx(HighlightByIndex, {
              index: "1",
              children: "plus pauvres"
            }), " allant à l’école en comparaison avec les ", _jsx(HighlightByIndex, {
              index: "0",
              children: "plus riches"
            }), " – représentés en bleu. Vous pouvez modifier le niveau d’enseignement depuis le primaire jusqu’au premier cycle de l’enseignement secondaire et au-delà en haut du graphique."]
          })
        }), _jsx(ScrollySection, {
          id: "PHL",
          children: _jsxs(_components.p, {
            children: ["Dans l’enseignement tertiaire, les écarts les plus importants concernent les pays à revenu intermédiaire. Ainsi, aux ", _jsx(_components.strong, {
              children: "Philippines"
            }), ", 61 % des plus riches mais moins de 20 % des plus pauvres ont suivi une forme quelconque d’enseignement post-secondaire."]
          })
        }), _jsx(ScrollySection, {
          id: "NPL",
          children: _jsxs(_components.p, {
            children: ["Au ", _jsx(_components.strong, {
              children: "Népal"
            }), ", ces chiffres s’élevaient respectivement à 48 % et 5 %."]
          })
        })]
      }), _jsx(_components.p, {
        children: "Il est encore plus frappant d’observer la façon dont ces écarts évoluent à mesure que les enfants poursuivent leurs études."
      }), _jsxs(_components.p, {
        children: ["L’examen ci-après des écarts ", _jsx(_components.strong, {
          children: "entre les sexes"
        }), " au ", _jsx(HighlightByIndex, {
          index: "0",
          children: "Burkina Faso"
        }), " montre à quel point les filles abandonnent l’école à mesure que le niveau d’enseignement s’élève."]
      }), _jsxs(_components.p, {
        children: ["Par ailleurs, sélectionner la ", _jsx(_components.strong, {
          children: "richesse"
        }), " montre combien les écarts s’accumulent au cours de la scolarité au détriment des plus pauvres, parmi lesquels très peu vont au-delà du second cycle du secondaire."]
      }), _jsx(Figure, {
        id: "WLGPI"
      })]
    }), "\n", _jsx(_components.p, {
      children: "Il est rare qu’un enfant ne relève que d’une seule catégorie, il est donc essentiel d’examiner aussi comment les caractéristiques individuelles, telles que le sexe, interagissent avec d’autres, telles que la richesse, comme l’illustre la figure suivante."
    }), "\n", _jsxs(_components.p, {
      children: ["En Afrique subsaharienne, les écarts entre les ", _jsx(HighlightByIndex, {
        index: "1",
        children: "plus pauvres"
      }), " et les ", _jsx(HighlightByIndex, {
        index: "0",
        children: "plus riches"
      }), " se creusent au cours de la scolarité, puisque pour 100 garçons, moins de 35 filles parmi les plus pauvres contre près de 100 parmi les plus riches poursuivent des études tertiaires."]
    }), "\n", _jsx(_components.p, {
      children: "En Asie de l’Est et du Sud-Est, en revanche, les points remontent en s’éloignant de la ligne grise car les écarts entre les sexes se font au détriment des garçons les plus pauvres. Au niveau de l’enseignement tertiaire, la fréquentation des filles les plus riches est environ 30 % supérieure à celle de leurs pairs masculins, tandis que celle des filles les plus pauvres est 40 % plus élevée."
    }), "\n", _jsx(Figure, {
      id: "GPIBYWEALTH"
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "Il existe des inégalités d’une ampleur inacceptable entre pays et entre certains groupes au sein de ces pays. Disposer de données ventilées de haute qualité est une condition indispensable pour concevoir les stratégies appropriées qui permettront de faire reculer ces inégalités et, au bout du compte, de ne laisser personne de côté dans nos efforts pour atteindre l’ODD 4."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.education-inequalities.org/",
            children: "Worldwide Inequalities Database on Education (WIDE)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/publication/2025-gender-report",
            children: "Women lead for learning – GEM Gender Report 2025"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024genderreport",
            children: "Technology on her terms – GEM Gender Report 2024"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.savethechildren.org.uk/content/dam/gb/reports/policy/still-left-behind-low-res.pdf",
            children: "Still left behind – Save the Children"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
